import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class IndexedDbService {

  private readonly DB_NAME = 'AuthDB';
  private readonly DB_VERSION = 1;
  private readonly STORE_NAME = 'tokens';

  private db: IDBDatabase | null = null;

  constructor() {
    this.initDB();
  }

  // Initialize DB
  private initDB(): void {
    const request = indexedDB.open(this.DB_NAME, this.DB_VERSION);

    request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
      const db = (event.target as IDBOpenDBRequest).result;

      if (!db.objectStoreNames.contains(this.STORE_NAME)) {
        db.createObjectStore(this.STORE_NAME);
      }
    };

    request.onsuccess = () => {
      this.db = request.result;
    };

    request.onerror = () => {
      console.error('❌ IndexedDB init failed');
    };
  }

  // Ensure DB is ready
  private getDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      if (this.db) {
        resolve(this.db);
        return;
      }

      const request = indexedDB.open(this.DB_NAME, this.DB_VERSION);

      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };

      request.onerror = () => reject('❌ DB open failed');
    });
  }

  // SAVE TOKEN
  async setToken(token: string): Promise<void> {
    const db = await this.getDB();

    return new Promise((resolve, reject) => {
      const tx = db.transaction(this.STORE_NAME, 'readwrite');
      const store = tx.objectStore(this.STORE_NAME);

      store.put(token, 'TOKEN');

      tx.oncomplete = () => resolve();
      tx.onerror = () => reject('❌ Token save failed');
    });
  }

  // GET TOKEN
  async getToken(): Promise<string | null> {
    const db = await this.getDB();

    return new Promise((resolve, reject) => {
      const tx = db.transaction(this.STORE_NAME, 'readonly');
      const store = tx.objectStore(this.STORE_NAME);

      const request = store.get('TOKEN');

      request.onsuccess = () => resolve(request.result ?? null);
      request.onerror = () => reject('❌ Token fetch failed');
    });
  }

  // REMOVE TOKEN
  async clearToken(): Promise<void> {
    const db = await this.getDB();

    return new Promise((resolve, reject) => {
      const tx = db.transaction(this.STORE_NAME, 'readwrite');
      const store = tx.objectStore(this.STORE_NAME);

      store.delete('TOKEN');

      tx.oncomplete = () => resolve();
      tx.onerror = () => reject('❌ Token delete failed');
    });
  }

  // CLEAR DB (logout / reset)
  async clearAll(): Promise<void> {
    const db = await this.getDB();

    return new Promise((resolve, reject) => {
      const tx = db.transaction(this.STORE_NAME, 'readwrite');
      tx.objectStore(this.STORE_NAME).clear();

      tx.oncomplete = () => resolve();
      tx.onerror = () => reject('❌ DB clear failed');
    });
  }
}
